import React, { useEffect, useState } from "react";
import { History, Search, SlidersHorizontal } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { techStacksList } from "../../utils/common";
import DataTable from "../Common/DataTable";
import { PageHeader } from "../ui";
import { FilterModal, FilterGroup } from "../ui/FilterModal";
import "./AI.scss";

const techStacks = techStacksList;
const difficulties = ["", "easy", "medium", "hard"];
const DEFAULT_PAGE_SIZE = 10;

const StudentAttemptHistory = () => {
  const student = getCurrentUser();
  const [attempts, setAttempts] = useState([]);
  const [filters, setFilters] = useState({
    techStack: "",
    difficulty: "",
    minScore: "",
    maxScore: ""
  });
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/ai/ai-attempts/history", {
        params: {
          studentId: student?.studentId,
          page,
          limit: pageSize,
          ...filters
        }
      });
      setAttempts(data.attempts || []);
      setTotalRecords(data.total || 0);
    } catch {
      setAttempts([]);
      setTotalRecords(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (student?.studentId) fetchAttempts();
  }, [filters, page, pageSize]);

  const handleResetFilters = () => {
    setFilters(f => ({ ...f, techStack: "", difficulty: "", minScore: "", maxScore: "" }));
    setPage(1);
  };

  const activeFilterCount = 
    (filters.techStack ? 1 : 0) + 
    (filters.difficulty ? 1 : 0) + 
    (filters.minScore ? 1 : 0) + 
    (filters.maxScore ? 1 : 0);

  const getScoreClass = (score) => {
    if (score >= 70) return "ai-score-badge--high";
    if (score >= 40) return "ai-score-badge--medium";
    return "ai-score-badge--low";
  };

  const getDifficultyClass = (difficulty) => {
    const d = difficulty?.toLowerCase();
    if (d === "easy") return "ai-difficulty-badge--easy";
    if (d === "medium") return "ai-difficulty-badge--medium";
    if (d === "hard") return "ai-difficulty-badge--hard";
    return "";
  };

  // Define table columns
  const columns = [
    {
      key: "index",
      label: "#",
      width: "50px",
      render: (_, index) => (page - 1) * pageSize + index + 1
    },
    {
      key: "question",
      label: "Question",
      width: "25%",
      render: (a) => (
        <div className="fw-medium" title={a.question?.question}>
          {a.question?.question?.length > 80 
            ? a.question.question.substring(0, 80) + "..." 
            : (a.question?.question || "-")}
        </div>
      )
    },
    {
      key: "answer",
      label: "Your Answer",
      width: "20%",
      render: (a) => (
        <div className="text-sm" title={a.answer}>
          {a.answer?.length > 80 ? a.answer.substring(0, 80) + "..." : (a.answer || "-")}
        </div>
      )
    },
    {
      key: "techStack",
      label: "Tech Stack",
      width: "10%",
      render: (a) => a.question?.techStack || "-"
    },
    {
      key: "difficulty",
      label: "Difficulty",
      width: "10%",
      render: (a) => (
        <span className={`ai-difficulty-badge ${getDifficultyClass(a.question?.difficulty)}`}>
          {a.question?.difficulty || "-"}
        </span>
      )
    },
    {
      key: "score",
      label: "Score",
      width: "8%",
      render: (a) => (
        <span className={`ai-score-badge ${getScoreClass(a.score)}`}>
          {a.score ?? "-"}
        </span>
      )
    },
    {
      key: "feedback",
      label: "Feedback",
      width: "20%",
      render: (a) => (
        <div className="text-sm" title={a.feedback}>
          {a.feedback?.length > 80 ? a.feedback.substring(0, 80) + "..." : (a.feedback || "-")}
        </div>
      )
    },
    {
      key: "submittedOn",
      label: "Submitted On",
      width: "12%",
      render: (a) => a.createdAt 
        ? new Date(a.createdAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) 
        : "-"
    }
  ];

  return (
    <div className="ai-page">
      <PageHeader
        icon={History}
        title="My AI Attempts"
        description="View your AI interview practice history and scores"
      />

      {/* Compact Toolbar */}
      <div className="ai-toolbar">
        <div className="ai-toolbar-actions" style={{ marginLeft: "auto" }}>
          {/* Desktop inline filters */}
          <select
            className="ai-filter-select d-none d-md-block"
            value={filters.techStack}
            onChange={(e) => {
              setFilters(f => ({ ...f, techStack: e.target.value }));
              setPage(1);
            }}
          >
            {techStacks.map((t) => (
              <option key={t} value={t}>{t || "All Tech Stacks"}</option>
            ))}
          </select>
          <select
            className="ai-filter-select d-none d-md-block"
            value={filters.difficulty}
            onChange={(e) => {
              setFilters(f => ({ ...f, difficulty: e.target.value }));
              setPage(1);
            }}
          >
            {difficulties.map((d) => (
              <option key={d} value={d}>{d || "All Difficulties"}</option>
            ))}
          </select>

          {/* Mobile filter button */}
          <button
            className={`ai-filter-btn d-md-none${activeFilterCount > 0 ? " ai-filter-btn--active" : ""}`}
            onClick={() => setShowFilterModal(true)}
            aria-label="Open filters"
          >
            <SlidersHorizontal size={16} />
            {activeFilterCount > 0 && (
              <span className="ai-filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={attempts}
        columns={columns}
        loading={loading}
        hoverable={true}
        pagination={{
          page,
          pageSize,
          total: totalRecords,
          onPageChange: setPage
        }}
        emptyMessage="No AI attempts found"
      />

      {/* Mobile Filter Modal */}
      <FilterModal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        onReset={handleResetFilters}
      >
        <FilterGroup label="Tech Stack">
          <select
            value={filters.techStack}
            onChange={(e) => {
              setFilters(f => ({ ...f, techStack: e.target.value }));
              setPage(1);
            }}
          >
            {techStacks.map((t) => (
              <option key={t} value={t}>{t || "All Tech Stacks"}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Difficulty">
          <select
            value={filters.difficulty}
            onChange={(e) => {
              setFilters(f => ({ ...f, difficulty: e.target.value }));
              setPage(1);
            }}
          >
            {difficulties.map((d) => (
              <option key={d} value={d}>{d || "All Difficulties"}</option>
            ))}
          </select>
        </FilterGroup>
        <FilterGroup label="Min Score">
          <input
            type="number"
            value={filters.minScore}
            onChange={(e) => {
              setFilters(f => ({ ...f, minScore: e.target.value }));
              setPage(1);
            }}
            placeholder="Min Score"
          />
        </FilterGroup>
        <FilterGroup label="Max Score">
          <input
            type="number"
            value={filters.maxScore}
            onChange={(e) => {
              setFilters(f => ({ ...f, maxScore: e.target.value }));
              setPage(1);
            }}
            placeholder="Max Score"
          />
        </FilterGroup>
      </FilterModal>
    </div>
  );
};

export default StudentAttemptHistory;
